import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PRODUCT_DISPLAY_NAME,
  generateFallColorMetaTags,
  updateDocumentHead,
} from '@/utils/seoUtils';
import { isFallColorMapPath } from '@/utils/map/fallColorShareUrl';

/**
 * Paths that must not be indexed (defense in depth beyond robots.txt).
 */
function pathRequiresNoIndex(pathname: string): boolean {
  if (pathname.startsWith('/maps')) return true;
  if (pathname.startsWith('/dashboard')) return true;
  if (pathname.startsWith('/settings')) return true;
  if (pathname === '/login' || pathname.startsWith('/login/')) return true;
  if (pathname === '/signup' || pathname.startsWith('/signup/')) return true;
  if (pathname.startsWith('/notifications')) return true;
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return true;
  if (pathname.startsWith('/hosts/')) return true;
  if (pathname.startsWith('/host/')) return true;
  if (pathname.startsWith('/observation/')) return true;
  if (pathname.startsWith('/admin')) return true;
  return false;
}

/**
 * Applies `noindex, nofollow` on app-shell / private routes.
 * Mount once under the React Router tree.
 */
export function useRouteSeoPolicy(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    // The one public map view: shared on social, so it carries real meta
    // (canonical `/fall-color`) instead of the app-shell noindex below.
    if (isFallColorMapPath(pathname)) {
      updateDocumentHead({ ...generateFallColorMetaTags(), robots: 'index' });
      return;
    }
    if (!pathRequiresNoIndex(pathname)) {
      return;
    }
    updateDocumentHead({
      title: PRODUCT_DISPLAY_NAME,
      description: PRODUCT_DISPLAY_NAME,
      ogTitle: PRODUCT_DISPLAY_NAME,
      ogDescription: PRODUCT_DISPLAY_NAME,
      ogType: 'website',
      twitterCard: 'summary',
      twitterTitle: PRODUCT_DISPLAY_NAME,
      twitterDescription: PRODUCT_DISPLAY_NAME,
      robots: 'noindex, nofollow',
    });
  }, [pathname]);
}
